import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useNumbers } from '../context/NumberContext';

// Exact category keys from Meta docs (uppercase from our backend)
const CATEGORY_META = {
  MARKETING:                { label: 'Marketing',          color: '#f59e0b', icon: '📣', free: false },
  UTILITY:                  { label: 'Utility',            color: '#3b82f6', icon: '🔧', free: false },
  AUTHENTICATION:           { label: 'Authentication',     color: '#8b5cf6', icon: '🔐', free: false },
  AUTHENTICATION_INTERNATIONAL: { label: 'Auth Intl',     color: '#7c3aed', icon: '🔐', free: false },
  SERVICE:                  { label: 'Service',            color: '#10b981', icon: '💬', free: true  },
  REFERRAL_CONVERSION:      { label: 'Referral/Free Entry',color: '#06b6d4', icon: '🎁', free: true  },
  UNKNOWN:                  { label: 'Unknown',            color: '#6b7280', icon: '📨', free: false },
};

const PRICING_TYPE_META = {
  REGULAR:              { label: 'Regular (Paid)',           color: '#f59e0b' },
  FREE_CUSTOMER_SERVICE:{ label: 'Free (Service Window)',    color: '#10b981' },
  FREE_ENTRY_POINT:     { label: 'Free Entry Point',        color: '#06b6d4' },
};

const TIER_MAP = {
  TIER_250:       { label: 'Tier 1 · 250/day',     color: '#ef4444' },
  TIER_1K:        { label: 'Tier 2 · 1,000/day',   color: '#f59e0b' },
  TIER_10K:       { label: 'Tier 3 · 10,000/day',  color: '#3b82f6' },
  TIER_100K:      { label: 'Tier 4 · 100,000/day', color: '#10b981' },
  TIER_UNLIMITED: { label: 'Unlimited',             color: '#10b981' },
};


function currSymbol(currency) {
  const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', BRL: 'R$', MXN: 'MX$', IDR: 'Rp' };
  return symbols[currency] || (currency ? currency + ' ' : '$');
}

export default function Analytics() {
  const { activeNumber, activeBusiness, loaded } = useNumbers();
  const [days, setDays] = useState(30);

  const [numInfo,      setNumInfo]      = useState(null);
  const [pricingData,  setPricingData]  = useState(null);
  const [tplData,      setTplData]      = useState(null);
  const [msgData,      setMsgData]      = useState(null);
  const [webhookData,  setWebhookData]  = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [errors,       setErrors]       = useState({});

  const load = useCallback(async () => {
    if (!activeNumber) return;
    setLoading(true);
    setErrors({});
    setNumInfo(null); setPricingData(null); setTplData(null);
    setMsgData(null); setWebhookData(null);

    const end   = Math.floor(Date.now() / 1000);
    const start = end - days * 86400;
    const id    = activeNumber.id;

    const [info, pricing, tpl, msg, webhook] = await Promise.allSettled([
      api.get(`/analytics/number-info?wa_number_id=${id}`),
      api.get(`/analytics/pricing?wa_number_id=${id}&start=${start}&end=${end}`),
      api.get(`/analytics/templates?wa_number_id=${id}&start=${start}&end=${end}`),
      api.get(`/analytics/messaging?wa_number_id=${id}&start=${start}&end=${end}`),
      api.get(`/analytics/webhook-pricing?wa_number_id=${id}&days=${days}`),
    ]);

    const extractErr = (r) => r.reason?.response?.data?.error || r.reason?.message || 'Request failed';

    if (info.status    === 'fulfilled') setNumInfo(info.value.data);
    else setErrors(e => ({ ...e, info: extractErr(info) }));

    if (pricing.status === 'fulfilled') setPricingData(pricing.value.data);
    else setErrors(e => ({ ...e, pricing: extractErr(pricing) }));

    if (tpl.status     === 'fulfilled') setTplData(tpl.value.data);
    else setErrors(e => ({ ...e, tpl: extractErr(tpl) }));

    if (msg.status     === 'fulfilled') setMsgData(msg.value.data);
    else setErrors(e => ({ ...e, msg: extractErr(msg) }));

    if (webhook.status === 'fulfilled') setWebhookData(webhook.value.data);
    else setErrors(e => ({ ...e, webhook: extractErr(webhook) }));

    setLoading(false);
  }, [activeNumber, days]);

  useEffect(() => { load(); }, [load]);

  if (!loaded) return <Center>Loading…</Center>;
  if (!activeNumber) return (
    <Center>
      <div style={{ fontSize: 48, marginBottom: 14 }}>📊</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Select a Number</div>
      <div style={{ fontSize: 13, color: 'var(--text2)' }}>Choose a business and phone number from the sidebar.</div>
    </Center>
  );

  const tier    = numInfo ? (TIER_MAP[numInfo.messaging_limit_tier] || TIER_MAP[numInfo.whatsapp_business_manager_messaging_limit]) : null;
  const quality = numInfo?.quality_rating;
  const qualityColor = quality === 'GREEN' ? '#10b981' : quality === 'YELLOW' ? '#f59e0b' : quality ? '#ef4444' : 'var(--text3)';

  return (
    <div style={{ padding: '20px 16px', maxWidth: 960, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, letterSpacing: '0.06em', color: 'var(--text2)', margin: 0 }}>
            📊 ANALYTICS & BILLING
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
            {activeBusiness?.label} → {activeNumber.label}
            {numInfo?.display_phone_number && ` · ${numInfo.display_phone_number}`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            All pricing data fetched directly from Meta API
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={days === d ? 'btn-primary' : 'btn-ghost'}
              style={{ fontSize: 12, padding: '5px 14px' }}>{d}d</button>
          ))}
          <button onClick={load} disabled={loading} className="btn-ghost" style={{ fontSize: 12 }}>
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {/* ── Row 1: Phone number status cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
        <StatCard icon="📱" label="Messaging Tier"
          value={tier?.label || (errors.info ? 'Error' : loading ? '…' : '—')}
          color={tier?.color} />
        <StatCard icon="⭐" label="Quality Rating"
          value={quality || (errors.info ? 'Error' : loading ? '…' : '—')}
          color={qualityColor} />
        <StatCard icon="✅" label="Status"
          value={numInfo?.status || (loading ? '…' : '—')}
          color={numInfo?.status === 'CONNECTED' ? '#10b981' : undefined} />
        <StatCard icon="📤" label="Sent (Meta)" value={fmt(msgData?.totals?.sent)} sub={`last ${days}d`} />
        <StatCard icon="📨" label="Delivered (Meta)" value={fmt(msgData?.totals?.delivered)} sub={`last ${days}d`} />
        <StatCard icon="💵" label="Total Cost"
          value={pricingData ? `${currSymbol(pricingData?.currency)}${(pricingData.total_cost || 0).toFixed(2)}` : (errors.pricing ? '—' : loading ? '…' : '—')}
          color={pricingData?.total_cost > 0 ? '#f59e0b' : '#10b981'} />
      </div>

      {errors.info && <ErrBox msg={errors.info} />}

      {/* ── Row 2: pricing_analytics breakdown ── */}
      <Section
        title="// BILLING BREAKDOWN · Meta pricing_analytics API"
        subtitle="Per-message pricing (PMP model, July 2025+) — fetched live from Meta">

        {errors.pricing && (
          <ErrBox msg={errors.pricing}
            hint="Token needs 'whatsapp_business_management' permission. Go to Meta Business Manager → System Users → Edit permissions." />
        )}
        {loading && !pricingData && <Loading />}

        {pricingData && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
              <StatCard icon="📨" label="Total Messages" value={fmt(pricingData.total_volume)} />
              <StatCard icon="💵" label={`Total Cost (${pricingData?.currency || "..."})`}
                value={`$${(pricingData.total_cost_usd || 0).toFixed(4)}`}
                color={pricingData.total_cost_usd > 0 ? '#f59e0b' : '#10b981'} />
              <StatCard icon="✅" label="Free Messages"
                value={fmt(Object.entries(pricingData.by_category || {})
                  .filter(([k]) => CATEGORY_META[k]?.free)
                  .reduce((s, [, v]) => s + (v.volume || 0), 0))}
                color="#10b981" />
              <StatCard icon="💳" label="Paid Messages"
                value={fmt(Object.entries(pricingData.by_category || {})
                  .filter(([k]) => !CATEGORY_META[k]?.free)
                  .reduce((s, [, v]) => s + (v.volume || 0), 0))}
                color="#f59e0b" />
            </div>

            {Object.keys(pricingData.by_category || {}).length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                {Object.entries(pricingData.by_category).map(([cat, data]) => {
                  const meta = CATEGORY_META[cat] || CATEGORY_META.UNKNOWN;
                  const pct  = pricingData.total_volume > 0
                    ? Math.round((data.volume / pricingData.total_volume) * 100) : 0;
                  return (
                    <div key={cat} style={{ padding: 14, background: 'var(--bg3)',
                      borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
                      borderLeft: `4px solid ${meta.color}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{meta.icon} {meta.label}</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: meta.color }}>{fmt(data.volume)}</span>
                      </div>
                      {/* Show by pricing type */}
                      {data.by_type && Object.entries(data.by_type).map(([type, td]) => {
                        const tm = PRICING_TYPE_META[type] || { label: type, color: '#888' };
                        return (
                          <div key={type} style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>
                            <span style={{ color: tm.color }}>●</span> {tm.label}: {fmt(td.volume)}
                            {td.cost > 0 && <span style={{ color: '#f59e0b', marginLeft: 6 }}>{currSymbol(pricingData?.currency)}{td.cost.toFixed(2)}</span>}
                            {td.cost === 0 && <span style={{ color: '#10b981', marginLeft: 6 }}>FREE</span>}
                          </div>
                        );
                      })}
                      <div style={{ marginTop: 8, fontSize: 12 }}>
                        Total Cost: {data.cost > 0
                          ? <strong style={{ color: '#f59e0b' }}>{currSymbol(pricingData?.currency)}{data.cost.toFixed(2)}</strong>
                          : <strong style={{ color: '#10b981' }}>FREE</strong>}
                      </div>
                      <div style={{ marginTop: 6, height: 4, background: 'var(--border)', borderRadius: 2 }}>
                        <div style={{ height: '100%', borderRadius: 2, background: meta.color,
                          width: `${pct}%`, transition: 'width 0.4s' }} />
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{pct}% of total</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>
                No messaging data for this period from Meta API.
              </div>
            )}
          </>
        )}
      </Section>

      {/* ── Row 3: Webhook-captured pricing (our DB) ── */}
      <Section
        title="// WEBHOOK-CAPTURED PRICING · Real-time from Meta Status Callbacks"
        subtitle="Captured from Meta's delivered status webhooks — per-message pricing object stored in our DB">

        {loading && !webhookData && <Loading />}
        {errors.webhook && <ErrBox msg={errors.webhook} />}

        {webhookData && (
          <>
            {webhookData._note && (
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12,
                padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--r-sm)',
                border: '1px solid var(--border)' }}>
                ℹ️ {webhookData._note}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
              <StatCard icon="📤" label="Priced Messages" value={fmt(webhookData.totals?.volume)} />
              <StatCard icon="💵" label="Cost"
                value={pricingData?.currency ? `${currSymbol(pricingData.currency)}${(webhookData.totals?.cost_usd||0).toFixed(2)}` : '—'}
                color="#f59e0b" />
              <StatCard icon="✅" label="Free"
                value={fmt(webhookData.totals?.free)} color="#10b981" />
              <StatCard icon="💳" label="Paid"
                value={fmt(webhookData.totals?.paid)} color="#f59e0b" />
            </div>

            {(webhookData.by_category || []).length > 0 ? (
              <>
                <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Category', 'Volume', 'Paid (charged)', 'Free', 'Billable Status'].map(h => (
                          <th key={h} style={{ padding: '7px 12px', textAlign: 'left',
                            color: 'var(--text3)', fontSize: 11, fontWeight: 500 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {webhookData.by_category.map((row, i) => {
                        const meta = CATEGORY_META[row.category] || CATEGORY_META.UNKNOWN;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border)',
                            background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <span style={{ color: meta.color }}>{meta.icon}</span>
                              <span style={{ marginLeft: 6, fontWeight: 500 }}>{meta.label}</span>
                            </td>
                            <td style={{ padding: '8px 12px' }}>{fmt(row.volume)}</td>
                            <td style={{ padding: '8px 12px', color: '#f59e0b' }}>{fmt(row.paid_count)}</td>
                            <td style={{ padding: '8px 12px', color: '#10b981' }}>{fmt(row.free_count)}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                              {parseFloat(row.total_cost || 0) === 0
                                ? <span style={{ color: '#10b981' }}>$0.00 FREE</span>
                                : <span style={{ color: '#f59e0b' }}>${parseFloat(row.total_cost).toFixed(4)}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {(webhookData.by_template || []).length > 0 && (
                  <>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
                      COST BY TEMPLATE
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            {['Template', 'Category', 'Sent', 'Paid', 'Free', 'Total Cost'].map(h => (
                              <th key={h} style={{ padding: '7px 12px', textAlign: 'left',
                                color: 'var(--text3)', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {webhookData.by_template.map((t, i) => {
                            const meta = CATEGORY_META[t.category] || CATEGORY_META.UNKNOWN;
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid var(--border)',
                                background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                                <td style={{ padding: '8px 12px' }}>
                                  <code style={{ fontSize: 12, color: 'var(--accent2)' }}>{t.template_name || '—'}</code>
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                  <span style={{ color: meta.color, fontSize: 12 }}>{meta.icon} {meta.label}</span>
                                </td>
                                <td style={{ padding: '8px 12px' }}>{fmt(t.sent)}</td>
                                <td style={{ padding: '8px 12px', color: '#f59e0b' }}>{fmt(t.paid_count)}</td>
                                <td style={{ padding: '8px 12px', color: '#10b981' }}>{fmt(t.free_count)}</td>
                                <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                                  {parseFloat(t.total_cost || 0) === 0
                                    ? <span style={{ color: '#10b981' }}>$0.00</span>
                                    : <span style={{ color: '#f59e0b' }}>${parseFloat(t.total_cost).toFixed(4)}</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>
                No webhook-captured pricing yet. Pricing data comes from Meta's delivered-status webhooks on template messages.
              </div>
            )}
          </>
        )}
      </Section>

      {/* ── Row 4: Template performance (Meta template_analytics) ── */}
      <Section
        title="// TEMPLATE PERFORMANCE · Meta template_analytics API"
        subtitle="Sent, delivered, read, clicked — official Meta data per template">

        {loading && !tplData && <Loading />}
        {errors.tpl && <ErrBox msg={errors.tpl}
          hint="Make sure templates are synced first (Templates → Sync). Also ensure insights are enabled via Graph API." />}

        {tplData?._hint && (
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10,
            padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--r-sm)' }}>
            ℹ️ {tplData._hint}
          </div>
        )}

        {tplData?.templates?.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Template', 'Category', 'Sent', 'Delivered', 'Read', 'Clicked', 'Cost', 'Del%', 'Read%'].map(h => (
                    <th key={h} style={{ padding: '7px 12px', textAlign: 'left',
                      color: 'var(--text3)', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tplData.templates.map((t, i) => {
                  const meta = CATEGORY_META[t.template_category?.toUpperCase()] || CATEGORY_META.UNKNOWN;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)',
                      background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                      <td style={{ padding: '8px 12px' }}>
                        <code style={{ fontSize: 12, color: 'var(--accent2)' }}>{t.template_name}</code>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4,
                          background: meta.color + '20', color: meta.color,
                          border: `1px solid ${meta.color}40` }}>
                          {meta.icon} {meta.label}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px' }}>{fmt(t.sent)}</td>
                      <td style={{ padding: '8px 12px' }}>{fmt(t.delivered)}</td>
                      <td style={{ padding: '8px 12px' }}>{fmt(t.read)}</td>
                      <td style={{ padding: '8px 12px' }}>{fmt(t.clicked)}</td>
                      <td style={{ padding: '8px 12px' }}>
                        {t.cost > 0
                          ? <span style={{ color: '#f59e0b' }}>${t.cost.toFixed(4)}</span>
                          : <span style={{ color: '#10b981' }}>$0</span>}
                      </td>
                      <td style={{ padding: '8px 12px' }}><PctBar value={t.delivery_rate} color="#3b82f6" /></td>
                      <td style={{ padding: '8px 12px' }}><PctBar value={t.read_rate} color="#10b981" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {tplData?.templates?.length === 0 && !errors.tpl && (
          <div style={{ color: 'var(--text3)', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>
            {tplData._hint || 'No template analytics for this period. Templates must be synced and insights enabled.'}
          </div>
        )}
      </Section>

      {/* Pricing note */}
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
        padding: '14px 16px', fontSize: 12, color: 'var(--text3)', lineHeight: 1.9 }}>
        <div style={{ color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>💡 Meta Pricing Model (July 2025+)</div>
        <div>📣 <strong>Marketing</strong> — charged per delivered msg</div>
        <div>🔧 <strong>Utility</strong> — charged at lower rate | outside customer service window</div>
        <div>🔐 <strong>Authentication</strong> — charged at lowest rate | 🔐 <strong>Auth Intl</strong> — different intl rates</div>
        <div>💬 <strong>Service</strong> — <strong style={{ color: '#10b981' }}>FREE</strong> (any non-template msg, no cap)</div>
        <div>🎁 <strong>Referral/Free Entry</strong> — <strong style={{ color: '#10b981' }}>FREE 72h</strong> window</div>
        <div>🔧 <strong>Utility inside open service window</strong> — <strong style={{ color: '#10b981' }}>FREE</strong></div>
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', fontSize: 11 }}>
          💱 Cost currency = your WABA's billing currency (set in Meta Business Manager → Billing).
          To change to INR: POST to <code>/&#123;WABA-ID&#125;?billing_currency=INR</code> via Graph API.
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.04em' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--bg3)',
      borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{icon} {label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || 'var(--text)', lineHeight: 1.2 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function PctBar({ value, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 3, minWidth: 40 }}>
        <div style={{ height: '100%', borderRadius: 3, background: color, width: `${Math.min(100, value || 0)}%` }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text2)', minWidth: 28 }}>{value || 0}%</span>
    </div>
  );
}

function ErrBox({ msg, hint }) {
  return (
    <div style={{ padding: '10px 14px', borderRadius: 'var(--r-sm)', marginBottom: 12,
      background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', fontSize: 12 }}>
      <div style={{ color: 'var(--danger)', fontWeight: 600 }}>⚠️ {msg}</div>
      {hint && <div style={{ color: 'var(--text3)', marginTop: 4 }}>💡 {hint}</div>}
    </div>
  );
}

function Loading() {
  return <div style={{ color: 'var(--text3)', fontSize: 12, padding: '12px 0' }}>Fetching from Meta API…</div>;
}

function Center({ children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', gap: 10, padding: 40, textAlign: 'center' }}>
      {children}
    </div>
  );
}

function fmt(n) {
  const num = parseInt(n);
  if (isNaN(num) || n == null) return '—';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000)    return (num / 1000).toFixed(1)    + 'K';
  return String(num);
}
